export const SUBSCRIPTION_TIER_ORDER = ['silver', 'gold', 'platinum', 'diamond'] as const;

export type SubscriptionTierKey = typeof SUBSCRIPTION_TIER_ORDER[number];

const SUBSCRIPTION_TIER_LABELS: Record<SubscriptionTierKey, string> = {
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  diamond: 'Diamond',
};

const SUBSCRIPTION_TIER_ALIASES: Record<string, SubscriptionTierKey> = {
  diamonds: 'diamond',
};

export const normalizeSubscriptionTierName = (name?: string | null): SubscriptionTierKey | null => {
  if (!name) return null;

  const normalized = name.trim().toLowerCase().replace(/\s+/g, ' ');
  const tierKey = SUBSCRIPTION_TIER_ALIASES[normalized] || normalized;

  return SUBSCRIPTION_TIER_ORDER.includes(tierKey as SubscriptionTierKey)
    ? (tierKey as SubscriptionTierKey)
    : null;
};

export const getSubscriptionTierLabel = (tierOrName?: string | null): string | null => {
  const tierKey = normalizeSubscriptionTierName(tierOrName);
  return tierKey ? SUBSCRIPTION_TIER_LABELS[tierKey] : null;
};

export const getSubscriptionTierRank = (tierOrName?: string | null): number => {
  const tierKey = normalizeSubscriptionTierName(tierOrName);
  return tierKey ? SUBSCRIPTION_TIER_ORDER.indexOf(tierKey) : Number.MAX_SAFE_INTEGER;
};

export const sortSubscriptionPlansByTier = <T extends { name?: string | null; price?: number; id?: number }>(
  plans: T[],
): T[] => {
  return [...plans].sort((left, right) => {
    const priceDiff = (left.price || 0) - (right.price || 0);
    if (priceDiff !== 0) return priceDiff;

    const nameDiff = (left.name || '').localeCompare(right.name || '');
    if (nameDiff !== 0) return nameDiff;

    return (left.id || 0) - (right.id || 0);
  });
};

export const sortActivePlansByTier = <
  T extends { plan_name?: string | null; plan_id?: number | null; id?: number; started_at?: string; expires_at?: string }
>(
  plans: T[],
): T[] => {
  return [...plans].sort((left, right) => {
    const startedAtDiff = new Date(left.started_at || 0).getTime() - new Date(right.started_at || 0).getTime();
    if (startedAtDiff !== 0) return startedAtDiff;

    const expiresAtDiff = new Date(left.expires_at || 0).getTime() - new Date(right.expires_at || 0).getTime();
    if (expiresAtDiff !== 0) return expiresAtDiff;

    const nameDiff = (left.plan_name || '').localeCompare(right.plan_name || '');
    if (nameDiff !== 0) return nameDiff;

    const planIdDiff = (left.plan_id || 0) - (right.plan_id || 0);
    if (planIdDiff !== 0) return planIdDiff;

    return (left.id || 0) - (right.id || 0);
  });
};

export const getNextSubscriptionTierKey = (
  plans: Array<{ name?: string | null }>,
): SubscriptionTierKey | null => {
  const existingTiers = new Set(
    plans
      .map((plan) => normalizeSubscriptionTierName(plan.name))
      .filter((tierKey): tierKey is SubscriptionTierKey => Boolean(tierKey)),
  );

  return SUBSCRIPTION_TIER_ORDER.find((tierKey) => !existingTiers.has(tierKey)) || null;
};

export const getNextSubscriptionTierLabel = (
  plans: Array<{ name?: string | null }>,
): string | null => {
  const tierKey = getNextSubscriptionTierKey(plans);
  return tierKey ? SUBSCRIPTION_TIER_LABELS[tierKey] : null;
};

export const hasAllStandardSubscriptionTiers = (
  plans: Array<{ name?: string | null }>,
): boolean => getNextSubscriptionTierKey(plans) === null;
